import React, { useEffect, useState } from "react";
import api, { formatMoney, formatDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, Plus, Trash2, Pencil } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const empty = { bank_name: "", account_name: "", iban: "", currency: "TRY", opening_balance: 0 };

export default function BankAccounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  async function load() {
    const { data } = await api.get("/bank-accounts");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  function openNew() { setForm(empty); setEditId(null); setOpen(true); }
  function openEdit(b) { setForm({ ...empty, ...b }); setEditId(b.id); setOpen(true); }

  async function save() {
    try {
      const payload = { ...form, opening_balance: Number(form.opening_balance) };
      if (editId) await api.put(`/bank-accounts/${editId}`, payload);
      else await api.post("/bank-accounts", payload);
      toast.success("Kaydedildi");
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function remove(id) {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/bank-accounts/${id}`);
    toast.success("Silindi");
    await load();
  }

  const totals = items.reduce((acc, b) => {
    acc[b.currency] = (acc[b.currency] || 0) + b.opening_balance;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="banks-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">Kasa & Bankalar</div>
          <h1 className="text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <Wallet className="w-8 h-8 text-amber-400" /> Banka Hesapları
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Çoklu para birimi destekli hesap ve kasa bakiyeleriniz.</p>
        </div>
        <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="add-bank-btn">
          <Plus className="w-4 h-4 mr-1" /> Hesap Ekle
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {["TRY", "USD", "EUR"].map((cur) => (
          <div key={cur} className="card-elevated rounded-2xl p-5 border border-amber-500/20">
            <div className="text-xs mono uppercase tracking-widest text-amber-300">{cur === "TRY" ? "TL Kasa" : `${cur} Kasa`}</div>
            <div className="text-3xl font-bold tabular mt-2">{formatMoney(totals[cur] || 0, cur)}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((b) => (
          <div key={b.id} className="card-elevated rounded-2xl p-5 relative group" data-testid={`bank-card-${b.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-lg font-semibold">{b.bank_name}</div>
                <div className="text-xs text-slate-400">{b.account_name}</div>
              </div>
              <Badge cur={b.currency} />
            </div>
            <div className="mt-4 mono text-xs text-slate-500 tracking-wider">{b.iban || "IBAN yok"}</div>
            <div className="mt-3 pt-3 border-t border-white/5">
              <div className="text-[10px] mono uppercase tracking-widest text-slate-500">Bakiye</div>
              <div className="text-2xl font-bold tabular mt-1">{formatMoney(b.opening_balance, b.currency)}</div>
            </div>
            <div className="absolute top-3 right-3 flex opacity-0 group-hover:opacity-100 transition">
              <Button size="sm" variant="ghost" onClick={() => openEdit(b)}><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => remove(b.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full p-10 text-center text-slate-500 card-elevated rounded-2xl">Henüz hesap eklenmemiş</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B111C] border-white/10 max-w-md" data-testid="bank-dialog">
          <DialogHeader><DialogTitle>{editId ? "Hesap Düzenle" : "Yeni Banka Hesabı"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Banka Adı</Label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="bank-name-input" />
            </div>
            <div>
              <Label>Hesap Adı</Label>
              <Input value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
            </div>
            <div>
              <Label>IBAN</Label>
              <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Para Birimi</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="bg-[#111723] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0B111C] border-white/10">
                    <SelectItem value="TRY">TL</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bakiye</Label>
                <Input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="bank-balance-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">İptal</Button>
            <Button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="save-bank-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Badge({ cur }) {
  const styles = {
    TRY: "bg-amber-500/15 text-amber-300 border-amber-500/25",
    USD: "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
    EUR: "bg-blue-500/15 text-blue-300 border-blue-500/25",
  };
  return <span className={`px-2.5 py-1 rounded-md border text-[10px] mono uppercase tracking-widest ${styles[cur]}`}>{cur}</span>;
}
